export enum AppPhase {
  AUTHENTICATION,
  DASHBOARD,
  PLAN,
  SCHEDULE,
  CLASSROOM,
  CLASS_PRESENTER,
  GRADE_COURSE_PICKER,
  GRADE_SCAN_SETUP,
  /** Organize captured/uploaded pages before review (paper workflow). */
  PAPER_ORGANIZE,
  GRADE_BATCH_ROSTER,
  ASSIGNMENT_SELECT,
  RUBRIC_SETUP,
  MODE_SELECTION,
  GRADING_LOOP,
  AUDIT,
  GRADE_SYNC_PREFLIGHT,
  SYNCING,
  FINALE,
  RECORDS,
  ROSTER_VIEW,
  ASSIGNMENT_CREATION,
  PAYWALL,
  OPTIONS,
  /** Create a new course (Google Classroom or local). */
  COURSE_CREATION,
  /** Paper scan + AI grading workflow (server-backed). */
  SCAN_WORKFLOW,
  // --- App-shell phases (Phase 1A foundation; gated behind VITE_APP_SHELL). ---
  // These are NOT wired into runtime routing/rendering yet. See lib/appShellMode.ts
  // and docs/DASHBOARD_APP_NAVIGATION_PLAN.md.
  /** App-shell dashboard landing. */
  HOME,
  /** App-shell Grade workspace home (launch point for the focused grading workflow). */
  GRADE_HOME,
  /** App-shell Plan workspace home. */
  PLAN_HOME,
  /** App-shell Communicate workspace home. */
  COMMUNICATE_HOME,
  /** App-shell Manage workspace home. */
  MANAGE_HOME,
  // --- Onboarding 1C: lightweight "Try it in 60 seconds" demo workflow. ---
  // Appended at the END so existing numeric phase values never shift. Gated
  // behind VITE_APP_SHELL + VITE_DEMO_WORKFLOW; inert when either is off.
  // See lib/demoWorkflowMode.ts and docs/DEMO_MODE_INVESTIGATION_AND_PLAN.md.
  /** Signed-in, no-Google, in-memory demo of paper → review → feedback → next step. */
  DEMO_WORKFLOW,
  // --- Privacy 6B: static Privacy Dashboard shell (Manage sub-screen). ---
  // Appended at the END so existing numeric phase values never shift. Gated
  // behind VITE_APP_SHELL; reached only from the Manage privacy card. Read-only
  // and non-destructive. See docs/PRIVACY_DASHBOARD_AUDIT_AND_PLAN.md.
  /** App-shell Manage → Privacy Dashboard (static, non-destructive overview). */
  PRIVACY_DASHBOARD,
  // --- Privacy 6F: Compliance status sub-screen (Manage → Privacy Dashboard). ---
  // Appended at the END so existing numeric phase values never shift. Gated
  // behind VITE_APP_SHELL; reached only from the Privacy Dashboard. Static
  // readiness overview — not legal certification.
  /** App-shell Manage → Privacy Dashboard → Compliance status (readiness only). */
  COMPLIANCE_STATUS,
}

export enum GradingMode {
  SINGLE_PAGE = 'SINGLE_PAGE',
  MULTI_PAGE = 'MULTI_PAGE',
}

export interface Student {
  id: string;
  name: string;
  email?: string;
  lastUsed?: number;
}

export interface Course {
  id: string;
  name: string;
  period: string;
  // Where this course comes from: 'google' = Google Classroom, 'local' = app-only
  source?: 'google' | 'local';
  lastUsed?: number;
}

export interface Assignment {
  id: string;
  title: string;
  maxScore: number;
  rubric: string;
  lastUsed?: number;
}

export interface BoundingBox {
  ymin: number;
  xmin: number;
  ymax: number;
  xmax: number;
}

// Added GeometricData interface to fix import error in App.tsx
export interface GeometricData {
  topLeft: number[];
  topRight: number[];
  bottomLeft: number[];
  bottomRight: number[];
}

export interface GradedWork {
  studentId: string;
  studentName: string;
  studentEmail?: string;
  score: number;
  maxScore: number;
  feedback: string;
  imageUrls: string[];
  status: 'draft' | 'synced';
  timestamp: number;
  courseName: string;
  assignmentName: string;
  courseId: string;
  assignmentId: string;
  // Added fields to support real-time scanning feedback and audit details
  scanHealth?: number;
  transcription?: string;
  geometry?: GeometricData;
}

export type SubscriptionStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled';

export interface GradingResponse {
  detected: boolean;
  studentName: string;
  score: number;
  feedback: string;
  boundingBox?: BoundingBox;
  confidence: number;
  // Added fields to match the document intelligence schema used in geminiService.ts
  scanHealth?: number;
  alignment?: string;
  corners?: GeometricData;
  triggerSignal?: string;
  oneWordCommand?: string;
  transcription?: string;
}
